/**
 * Marketplace Design System Colors
 *
 * Built on top of the unified resident color system.
 * All colors reference the central residentColors.ts for consistency.
 */
import { useMemo } from 'react';
import { PRIMITIVE_COLORS, SEMANTIC_COLORS, COMPONENT_COLORS } from './residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

export const MARKETPLACE_COLORS = {
  // Backgrounds
  background: SEMANTIC_COLORS.background.primary,
  cardBackground: SEMANTIC_COLORS.background.secondary,
  modalBackground: SEMANTIC_COLORS.background.secondary,
  inputBackground: SEMANTIC_COLORS.background.input,
  uploadAreaBackground: SEMANTIC_COLORS.background.tertiary,
  toggleBackground: COMPONENT_COLORS.toggle.background,
  toggleActiveBackground: COMPONENT_COLORS.toggle.activeBackground,
  optionItemBackground: SEMANTIC_COLORS.background.tertiary,
  
  // Text
  textPrimary: SEMANTIC_COLORS.text.primary,
  textSecondary: PRIMITIVE_COLORS.gray300,
  textMuted: PRIMITIVE_COLORS.gray400,
  textLight: SEMANTIC_COLORS.text.light,
  
  // Accents
  accentRed: PRIMITIVE_COLORS.resioPink,
  accentBlue: PRIMITIVE_COLORS.resioBlueLight,
  accentBlueBg: PRIMITIVE_COLORS.resioBlueBg,
  accentBlueSolid: PRIMITIVE_COLORS.resioBlue,
  
  // Borders & Dividers
  borderGray: PRIMITIVE_COLORS.gray800,
  dividerGray: PRIMITIVE_COLORS.gray700,
  inputBorder: PRIMITIVE_COLORS.gray500,
  modalBorder: PRIMITIVE_COLORS.resioBlueBorder,
  
  // Buttons
  buttonPrimary: PRIMITIVE_COLORS.resioBlue,
  buttonPrimaryText: '#FFFFFF',
  buttonDisabled: COMPONENT_COLORS.button.disabledBg,
  buttonDisabledText: COMPONENT_COLORS.button.disabledText,
  
  // Badges & Overlays
  priceBadgeBg: COMPONENT_COLORS.badge.priceBg,
  carouselBg: COMPONENT_COLORS.carousel.background,
  
  // Transparency variants
  white5Percent: PRIMITIVE_COLORS.white5,
  white10Percent: PRIMITIVE_COLORS.white10,
  progressBarBackground: PRIMITIVE_COLORS.white10,
  uploadBorderWhite: PRIMITIVE_COLORS.white25,
  
  // Special colors
  starYellow: PRIMITIVE_COLORS.starYellow,
  black: SEMANTIC_COLORS.background.primary,
  
  // Carousel dots
  dotActive: COMPONENT_COLORS.carousel.dotActive,
  dotInactive: PRIMITIVE_COLORS.gray500,
  
  // Checkbox/Radio
  checkboxBackground: COMPONENT_COLORS.checkbox.background,
  checkboxBorder: COMPONENT_COLORS.checkbox.border,
  checkboxSelected: COMPONENT_COLORS.checkbox.selected,
  radioSelected: COMPONENT_COLORS.checkbox.selected,
  radioInner: COMPONENT_COLORS.checkbox.checkmark,
  
  // Status/Semantic colors
  dangerRed: PRIMITIVE_COLORS.resioDangerRed,
  productivityOrange: PRIMITIVE_COLORS.warningOrange,
  gray200: PRIMITIVE_COLORS.gray200,
  accentBlueLight: PRIMITIVE_COLORS.resioBlueBgLight,
} as const;

export type MarketplaceColor = keyof typeof MARKETPLACE_COLORS;

export function useMarketplaceColors() {
  const { semantic, components, colors } = useThemeColors();
  return useMemo(() => ({
    // Backgrounds
    background: semantic.background.primary,
    cardBackground: semantic.background.secondary,
    modalBackground: semantic.background.secondary,
    inputBackground: semantic.background.input,
    uploadAreaBackground: semantic.background.tertiary,
    toggleBackground: components.toggle.background,
    toggleActiveBackground: components.toggle.activeBackground,
    optionItemBackground: semantic.background.tertiary,

    // Text
    textPrimary: semantic.text.primary,
    textSecondary: semantic.text.secondary,
    textMuted: semantic.text.tertiary,
    textLight: semantic.text.light,

    // Accents
    accentRed: semantic.interactive.secondary,
    accentBlue: semantic.interactive.primaryHover,
    accentBlueBg: colors.accent.blueBgMedium,
    accentBlueSolid: semantic.interactive.primary,

    // Borders & Dividers
    borderGray: semantic.border.tertiary,
    dividerGray: semantic.border.secondary,
    inputBorder: semantic.border.input,
    modalBorder: semantic.border.primary,

    // Buttons
    buttonPrimary: components.button.primaryBg,
    buttonPrimaryText: components.button.primaryText,
    buttonDisabled: components.button.disabledBg,
    buttonDisabledText: components.button.disabledText,

    // Badges & Overlays
    priceBadgeBg: components.badge.priceBg,
    carouselBg: components.carousel.background,

    // Transparency variants
    white5Percent: colors.glass.buttonBg,
    white10Percent: colors.activityIcon.bg,
    progressBarBackground: colors.activityIcon.bg,
    uploadBorderWhite: colors.card.border,

    // Special colors
    starYellow: semantic.status.warning,
    black: semantic.background.primary,

    // Carousel dots
    dotActive: components.carousel.dotActive,
    dotInactive: components.carousel.dotInactive,

    // Checkbox/Radio
    checkboxBackground: components.checkbox.background,
    checkboxBorder: components.checkbox.border,
    checkboxSelected: components.checkbox.selected,
    radioSelected: components.checkbox.selected,
    radioInner: components.checkbox.checkmark,

    // Status/Semantic colors
    dangerRed: semantic.interactive.danger,
    productivityOrange: semantic.status.warning,
    gray200: semantic.text.secondary,
    accentBlueLight: colors.accent.blueBgLight,
  }), [semantic, components, colors]);
}
